import type { PlaidAccount } from "react-plaid-link";

import { Button } from "@alpacahq/ui/old";

import type { BillingBankAccount } from "~/v2/api/rest/billing";
import { PlaidIcon } from "~/v2/components/common/PlaidIcon";

interface PlaidCardProps {
  plaidAccount: PlaidAccount | undefined;
  bankAccount: BillingBankAccount | undefined;
  onLinkBankAccount: () => void;
  isLinkButtonDisabled: boolean;
  isLinkButtonLoading: boolean;
  onUpdatePaymentMethodClicked: () => void;
  isUpdatePaymentMethodDisabled: boolean;
  isUpdatePaymentMethodLoading: boolean;
  isEditable?: boolean;
}

export const PlaidCard = ({
  plaidAccount,
  bankAccount,
  onLinkBankAccount,
  isLinkButtonDisabled,
  isLinkButtonLoading,
  onUpdatePaymentMethodClicked,
  isUpdatePaymentMethodDisabled,
  isUpdatePaymentMethodLoading,
  isEditable = true,
}: PlaidCardProps) => (
  <section className="flex flex-col gap-3 rounded-lg border border-gray-300 bg-white p-3">
    <div className="flex items-center justify-between gap-4">
      <h3 className="m-0 p-0 text-left font-semibold text-base text-gray-800">
        Linked Account
      </h3>
      <PlaidIcon />
    </div>
    {
      // User has existing linked bank account
      bankAccount ? (
        <>
          <p className="m-0 p-0 text-gray-600 text-sm">
            {isEditable
              ? "To replace or edit this payment method, log in to your bank and authorize the link to fund your data subscription. Alpaca never receives your bank credentials."
              : "Bank account updates are temporarily unavailable. To change your payment method, select Credit / Debit Card."}
          </p>
          <div className="flex items-center justify-between gap-4">
            <p className="m-0 inline-flex items-center justify-start gap-1 p-0 text-gray-700 text-xs">
              <span className="font-semibold">{bankAccount.brand}</span> ending
              in{" "}
              <span className="inline-flex items-center justify-center rounded-full border border-gray-300 bg-gray-50 px-2 py-1 font-mono">
                {bankAccount.last_4}
              </span>
            </p>
            {isEditable && (
              <Button
                variant="v2/filled"
                size="v2/small"
                className="w-fit"
                onClick={onLinkBankAccount}
                disabled={isLinkButtonDisabled}
                loading={isLinkButtonLoading}
              >
                Edit Account
              </Button>
            )}
          </div>
        </>
      ) : // User has linked a Plaid account but must submit to actually update bankAccount
      plaidAccount ? (
        <>
          <p className="m-0 p-0 text-gray-600 text-sm">
            Your linked bank account will be charged:
          </p>
          <p className="m-0 inline-flex items-center justify-start gap-1 p-0 text-gray-700 text-xs">
            <span className="font-semibold">{plaidAccount.name}</span> ending in{" "}
            <span className="inline-flex items-center justify-center rounded-full border border-gray-300 bg-gray-50 px-2 py-1 font-mono">
              {plaidAccount.mask}
            </span>
          </p>
          <Button
            variant="v2/filled"
            size="v2/small"
            className="w-fit"
            onClick={onUpdatePaymentMethodClicked}
            disabled={isUpdatePaymentMethodDisabled}
            loading={isUpdatePaymentMethodLoading}
          >
            Save Payment
          </Button>
        </>
      ) : (
        // User has not linked a bank account and has not gone through plaid link process
        <>
          <p className="m-0 p-0 text-gray-600 text-sm">
            Log in to your bank and authorize the link to fund your Algo Trader
            Plus subscription now. Alpaca never receives your bank credentials.
          </p>
          <Button
            className="w-fit"
            variant="v2/filled"
            onClick={onLinkBankAccount}
            disabled={isLinkButtonDisabled}
            loading={isLinkButtonLoading}
          >
            Edit Account
          </Button>
        </>
      )
    }
  </section>
);
